<template>
  <div
    class="container-fluid py-3 py-md-4"
    :dir="$i18n.locale === 'ar' ? 'rtl' : 'ltr'"
  v-can="'exam_forms.display'">
    <!-- Navigation Tabs -->
    <div class="row mb-3 mb-md-4">
      <div class="col-12">
        <div class="card shadow-sm">
          <div class="card-body py-2 py-md-3">
            <div
              class="d-flex flex-column flex-md-row justify-content-md-between align-items-stretch gap-2 gap-md-3"
            >
              <button
                type="button"
                class="btn btn-sm flex-grow-1 flex-md-grow-0 w-100 w-md-auto"
                :class="currentView === 'create' ? 'btn-primary' : 'btn-outline-primary'"
                @click="currentView = 'create'"
                v-can="'exam_forms.create'"
              >
                <i class="fas fa-plus me-1 me-md-2"></i>
                {{ $t('exams.new_exam') }}
              </button>

              <div class="d-none d-md-block flex-grow-1"></div>

              <button
                type="button"
                class="btn btn-sm flex-grow-1 flex-md-grow-0 w-100 w-md-auto"
                :class="currentView === 'saved' ? 'btn-primary' : 'btn-outline-primary'"
                @click="currentView = 'saved'"
              >
                <i class="fas fa-folder me-1 me-md-2"></i>
                {{ $t('exams.saved_exams') }} ({{ savedExams.length }})
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Create Exam View -->
    <div v-if="currentView === 'create'">
      <!-- Progress Header -->
      <div class="row">
        <div class="col-12">
          <div class="card shadow-sm">
            <div class="card-body py-2 py-md-3">
              <div class="d-flex justify-content-between align-items-center">
                <h4 class="mb-0 text-primary h5 h4-md">
                  <i class="fas fa-file-alt me-1 me-md-2"></i>
                  {{ $t('exams.create_exam_title') }}
                </h4>
              </div>

              <!-- Step Indicators -->
              <div class="d-flex justify-content-center mt-2 mt-md-3">
                <div
                  class="d-flex flex-wrap justify-content-center align-items-center gap-1 gap-md-2"
                >
                  <div
                    v-for="(step, index) in steps"
                    :key="index"
                    class="step-indicator d-flex align-items-center"
                    :class="{ active: currentStep === index, completed: index < currentStep }"
                  >
                    <div class="step-circle">
                      <i v-if="index < currentStep" class="fas fa-check"></i>
                      <span v-else>{{ index + 1 }}</span>
                    </div>
                    <span class="step-label ms-1 ms-md-2 d-none d-sm-inline">
                      {{ step.title }}
                    </span>
                    <div
                      v-if="index < steps.length - 1"
                      class="step-line ms-1 ms-md-3"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Main Content -->
      <div class="row">
        <!-- Left Panel - Controls -->
        <div class="col-xl-5 col-lg-6 col-md-12 my-3 my-md-4">
          <div class="card h-100">
            <div class="bg-white">
              <h6 class="mb-0">
                <i :class="steps[currentStep].icon" class="me-1 me-md-2"></i>
                {{ steps[currentStep].title }}
              </h6>
            </div>

            <div class="card-body">
              <!-- Step 1: Form Settings -->
              <div v-if="currentStep === 0" class="form-step">
                <form @submit.prevent="validateAndProceed">
                  <div class="mb-3">
                    <label for="title" class="form-label">{{ $t('exams.title_label') }}</label>
                    <input
                      id="title"
                      type="text"
                      class="form-control borderless-input"
                      v-model="examForm.title"
                      :class="{ 'is-invalid': errors.title }"
                      :placeholder="$t('exams.title_placeholder')"
                      required
                    />
                    <div v-if="errors.title" class="invalid-feedback">
                      {{ errors.title[0] }}
                    </div>
                  </div>

                  <div class="mb-3">
                    <label for="class_id" class="form-label">{{ $t('exams.class_label') }}</label>
                    <select
                      id="class_id"
                      class="form-select borderless-input"
                      v-model="examForm.class_id"
                      :class="{ 'is-invalid': errors.class_id }"
                      @change="handleClassChange"
                      required
                    >
                      <option value="">{{ $t('exams.select_class') }}</option>
                      <option
                        v-for="classItem in isTeacher ? teacherClasses : classes"
                        :key="classItem.id"
                        :value="classItem.id"
                      >
                        {{ classItem.name }}
                      </option>
                    </select>
                    <div v-if="errors.class_id" class="invalid-feedback">
                      {{ errors.class_id[0] }}
                    </div>
                  </div>

                  <div class="mb-3">
                    <label for="subject_id" class="form-label">{{ $t('exams.subject_label') }}</label>
                    <select
                      id="subject_id"
                      class="form-select borderless-input"
                      v-model="examForm.subject_id"
                      :class="{ 'is-invalid': errors.subject_id }"
                      @change="handleSubjectChange"
                      required
                    >
                      <option value="">{{ $t('exams.select_subject') }}</option>
                      <option
                        v-for="subject in isTeacher ? teacherSubjects : subjects"
                        :key="subject.id"
                        :value="subject.id"
                      >
                        {{ subject?.name || '' }}
                      </option>
                    </select>
                    <div v-if="errors.subject_id" class="invalid-feedback">
                      {{ errors.subject_id[0] }}
                    </div>
                  </div>

                  <div class="mb-3" v-if="!isTeacher">
                    <label for="teacher_id" class="form-label">{{ $t('exams.teacher_label') }}</label>
                    <select
                      id="teacher_id"
                      class="form-select borderless-input"
                      v-model="examForm.teacher_id"
                      :class="{ 'is-invalid': errors.teacher_id }"
                      @change="fetchQuestionsBySubject"
                      required
                    >
                      <option value="">{{ $t('exams.select_teacher') }}</option>
                      <option
                        v-for="teacher in teachers"
                        :key="teacher.id"
                        :value="teacher.id"
                      >
                        {{ teacher?.username || '' }}
                      </option>
                    </select>
                    <div v-if="errors.teacher_id" class="invalid-feedback">
                      {{ errors.teacher_id[0] }}
                    </div>
                  </div>

                  <div class="mb-3">
                    <label for="date" class="form-label">{{ $t('exams.date_label') }}</label>
                    <input
                      id="date"
                      type="datetime-local"
                      class="form-control borderless-input"
                      v-model="examForm.date"
                      :class="{ 'is-invalid': errors.date }"
                      required
                    />
                    <div v-if="errors.date" class="invalid-feedback">
                      {{ errors.date[0] }}
                    </div>
                  </div>

                  <div class="mb-3">
                    <label for="difficulty_level" class="form-label">
                      {{ $t('exams.difficulty_label') }}
                    </label>
                    <select
                      id="difficulty_level"
                      class="form-select borderless-input"
                      v-model="examForm.difficulty_level"
                      :class="{ 'is-invalid': errors.difficulty_level }"
                      required
                    >
                      <option value="">{{ $t('exams.select_difficulty') }}</option>
                      <option value="easy">{{ $t('exams.easy') }}</option>
                      <option value="medium">{{ $t('exams.medium') }}</option>
                      <option value="hard">{{ $t('exams.hard') }}</option>
                    </select>
                    <div v-if="errors.difficulty_level" class="invalid-feedback">
                      {{ errors.difficulty_level[0] }}
                    </div>
                  </div>

                  <div class="mb-3">
                    <label for="end_title" class="form-label">
                      {{ $t('exams.end_title_label') }}
                    </label>
                    <input
                      id="end_title"
                      type="text"
                      class="form-control"
                      v-model="examForm.end_title"
                      :placeholder="$t('exams.end_title_placeholder')"
                    />
                  </div>

                  <div class="alert alert-secondary small">
                    <i class="fas fa-info-circle me-1 me-md-2"></i>
                    {{ $t('exams.progress_fill_fields') }}
                  </div>
                </form>
              </div>

              <!-- Step 2: Structure Builder -->
              <div v-else-if="currentStep === 1" class="structure-step">
                <div class="mb-3">
                  <h6>{{ $t('exams.structure_tools') }}</h6>
                  <div class="d-flex flex-wrap gap-2 mb-3">
                    <button
                      class="btn btn-sm btn-outline-primary flex-grow-1 flex-md-grow-0"
                      @click="openQuestionModal"
                    >
                      <i class="fas fa-question-circle me-1"></i>
                      {{ $t('exams.add_question') }}
                    </button>
                    <button
                      class="btn btn-sm btn-outline-secondary flex-grow-1 flex-md-grow-0"
                      @click="addTitleToStructure"
                    >
                      <i class="fas fa-heading me-1"></i>
                      {{ $t('exams.add_title') }}
                    </button>
                  </div>
                </div>

                <div class="structure-builder">
                  <h6>{{ $t('exams.structure_title') }}</h6>
                  <div class="structure-list">
                  <div
  v-for="(item, index) in examStructure"
  :key="index"
  class="structure-item mb-2 p-2 border rounded"
  @dragover.prevent="onDragOver($event, index)"
  @drop="onDrop($event, index)"
  :class="{ 'drag-over': hoverIndex === index }"
>
  <div class="d-flex align-items-center">
    <!-- drag handle (only this starts the drag) -->
    <i
      class="fas fa-grip-lines drag-handle me-2 text-muted"
      style="cursor: grab"
      draggable="true"
      @dragstart="onDragStart($event, index)"
      @dragend="onDragEnd"
    />
    <!-- rest unchanged -->
    <div class="flex-grow-1">
      <div v-if="item.type === 'title'">
        <span class="badge bg-primary me-1 me-md-2">
          {{ $t('exams.section_title') }}
        </span>
        <input
          type="text"
          class="form-control borderless-input form-control-sm d-inline-block"
          style="width:auto;min-width:150px;max-width:100%;"
          v-model="item.title_name"
          :placeholder="$t('exams.section_title')"
        />
      </div>
      <div v-else>
        <span class="badge bg-secondary me-1 me-md-2">
          {{ $t('exams.question') }}
        </span>
        <span class="text-muted small">
          {{ getQuestionPreview(item.question_id) }}
        </span>
      </div>
    </div>

    <button
      type="button"
      class="btn btn-danger btn-sm ms-1 ms-md-2"
      @click="removeStructureItem(index)"
    >
      <i class="fas fa-trash"></i>
    </button>
  </div>
</div>

                  </div>

                  <div
                    v-if="examStructure.length === 0"
                    class="text-center py-3 text-muted"
                  >
                    <i class="fas fa-plus-circle fa-2x mb-2"></i>
                    <p class="mb-0">{{ $t('exams.structure_add_elements') }}</p>
                  </div>
                </div>

                <div class="alert alert-warning mt-3 small">
                  <i class="fas fa-exclamation-triangle me-1 me-md-2"></i>
                  {{ $t('exams.structure_add_at_least_one') }}
                </div>
              </div>

              <!-- Step 3: Questions Management -->
              <div v-else-if="currentStep === 2" class="questions-step">
                <div class="mb-3">
                  <div class="row g-2 mb-3">
                    <div class="col-12">
                      <input
                        type="text"
                        class="form-control form-control-sm"
                        :placeholder="$t('exams.search_questions')"
                        v-model="questionSearch"
                      />
                    </div>
                    <div class="col-md-6 col-12">
                      <select
                        class="form-select form-select-sm"
                        v-model="selectedQuestionType"
                      >
                        <option value="">{{ $t('exams.all_types') }}</option>
                        <option
                          v-for="type in questionTypes"
                          :key="type.id"
                          :value="type.name"
                        >
                          {{ type.title }}
                        </option>
                      </select>
                    </div>
                    <div class="col-md-6 col-12">
                      <select
                        class="form-select form-select-sm"
                        v-model="selectedQuestionLevel"
                      >
                        <option value="">{{ $t('exams.all_levels') }}</option>
                        <option
                          v-for="level in questionLevels"
                          :key="level.id"
                          :value="level.name"
                        >
                          {{ level.name }}
                        </option>
                      </select>
                    </div>
                  </div>
                </div>

                <!-- Questions List -->
                <div class="questions-list" style="max-height: 400px; overflow-y: auto">
                  <div
                    v-for="question in filteredQuestions"
                    :key="question.id"
                    class="question-card mb-2 p-2 border rounded"
                    :class="{
                      'border-success': isQuestionInStructure(question.id),
                      'cursor-pointer': true
                    }"
                    @click="toggleQuestionInStructure(question.id)"
                  >
                    <div class="d-flex justify-content-between align-items-start">
                      <div class="flex-grow-1">
                        <div class="d-flex flex-wrap align-items-center mb-1 gap-1">
                          <span
                            class="badge me-1"
                            :class="getQuestionTypeClass(question.question_type?.name)"
                          >
                            {{ getQuestionTypeName(question.question_type?.id) }}
                          </span>
                          <span
                            class="badge me-1"
                            :class="getQuestionLevelClass(question.question_level?.name)"
                          >
                            {{ getQuestionLevelName(question.question_level?.id) }}
                          </span>
                          <i
                            v-if="isQuestionInStructure(question.id)"
                            class="fas fa-check-circle text-success"
                          />
                        </div>
                        <p class="mb-0 small">
                          {{ truncateText(question?.description || '', 80) }}
                        </p>
                      </div>
                      <button
                        class="btn btn-sm ms-1 ms-md-2"
                        :class="isQuestionInStructure(question.id) ? 'btn-outline-danger' : 'btn-outline-primary'"
                        @click.stop="toggleQuestionInStructure(question.id)"
                      >
                        <i :class="isQuestionInStructure(question.id) ? 'fas fa-minus' : 'fas fa-plus'"></i>
                      </button>
                    </div>
                  </div>

                  <div v-if="filteredQuestions.length === 0" class="text-center py-4 text-muted">
                    <i class="fas fa-search fa-2x mb-2"></i>
                    <p class="mb-0">{{ $t('exams.no_questions_found') }}</p>
                  </div>
                </div>
              </div>

              <!-- Step 4: Preview -->
              <div v-else-if="currentStep === 3" class="preview-step">
                <div class="mb-3">
                  <h6>{{ $t('exams.preview_options') }}</h6>
                  <div class="d-flex flex-wrap gap-2 mb-3">
                    <button
                      class="btn btn-sm flex-grow-1"
                      :class="previewMode === 'exam' ? 'btn-primary' : 'btn-outline-primary'"
                      @click="previewMode = 'exam'"
                    >
                      {{ $t('exams.exam_sheet') }}
                    </button>
                    <button
                      class="btn btn-sm flex-grow-1"
                      :class="previewMode === 'answers' ? 'btn-primary' : 'btn-outline-primary'"
                      @click="previewMode = 'answers'"
                    >
                      {{ $t('exams.answer_key') }}
                    </button>
                  </div>
                </div>

                <div class="export-options">
                  <h6>{{ $t('exams.export_options') }}</h6>
                  <div class="d-grid gap-2">
                    <button
                      class="btn btn-success btn-sm"
                      @click="saveExam"
                      :disabled="!canProceed || isSaving"
                    >
                      <i class="fas fa-save me-1 me-md-2"></i>
                      {{ isSaving ? $t('exams.saving') : $t('exams.save_exam') }}
                    </button>
                    <button class="btn btn-outline-info btn-sm" @click="exportToPDF">
                      <i class="fas fa-download me-1 me-md-2"></i>
                      {{ $t('exams.export_pdf') }}
                    </button>
                    <button class="btn btn-outline-secondary btn-sm" @click="printExam">
                      <i class="fas fa-print me-1 me-md-2"></i>
                      {{ $t('exams.print') }}
                    </button>
                  </div>
                </div>

                <div class="alert alert-success mt-3 small">
                  <i class="fas fa-check-circle me-1 me-md-2"></i>
                  {{ $t('exams.ready_for_save') }}
                </div>
              </div>

              <!-- Navigation Buttons -->
              <div class="d-flex justify-content-between mt-3 pt-3 border-top">
                <button
                  class="btn btn-outline-secondary btn-sm"
                  @click="previousStep"
                  :disabled="currentStep === 0"
                >
                  <i class="fas fa-arrow-right me-1 me-md-2"></i>
                  {{ $t('exams.previous') }}
                </button>

                <button
                  v-if="currentStep < steps.length - 1"
                  class="btn btn-primary btn-sm"
                  @click="nextStep"
                  :disabled="!canProceed"
                >
                  {{ $t('exams.next') }}
                  <i class="fas fa-arrow-left ms-1 ms-md-2"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Panel - Paper Preview (Bilingual AR/EN) -->
        <div class="col-xl-7 col-lg-6 col-md-12 my-3 my-md-4">
          <div class="card h-100">
            <div
              class="d-flex flex-column flex-sm-row justify-content-between align-items-center gap-2 m-4"
            >
              <h6 class="mb-0 text-center text-sm-start">
                <i class="fas fa-eye me-1 me-md-2"></i>
                {{ previewMode === 'exam' ? $t('exams.preview_exam') : $t('exams.preview_answers') }}
              </h6>
              <div class="btn-group" role="group" aria-label="Preview mode toggle">
                <button
                  class="btn btn-sm"
                  :class="previewMode === 'exam' ? 'btn-primary' : 'btn-outline-secondary'"
                  @click="previewMode = 'exam'"
                >
                  {{ $t('exams.exam_sheet') }}
                </button>
                <button
                  class="btn btn-sm"
                  :class="previewMode === 'answers' ? 'btn-primary' : 'btn-outline-secondary'"
                  @click="previewMode = 'answers'"
                >
                  {{ $t('exams.answer_key') }}
                </button>
              </div>
            </div>

            <div class="card-body p-0">
              <div class="paper-container" ref="paperContainer" style="height: 600px; overflow-y: auto">
                <!-- Exam Sheet Preview -->
                <div v-if="previewMode === 'exam'" class="exam-paper professional-exam p-3 p-md-4">
                  <!-- School/Institution Header -->
                  <div class="header text-center mb-3 mb-md-4">
                    <div class="exam-title h4 text-primary mb-2">
                      {{ examForm.title || $t('exams.title_placeholder') }}
                    </div>
                    <div class="exam-meta row small text-muted">
                      <div class="col-md-6 text-md-end mb-1 mb-md-0">
                        {{ examForm.subject_name || getSubjectName(examForm?.subject_id) || $t('exams.not_specified') }}
                        <strong>{{ $t('exams.teacher') }}:</strong>
                        {{ getTeacherName(examForm?.teacher_id) }}
                      </div>
                      <div class="col-md-6 text-md-start">
                        <strong>{{ $t('exams.date') }}:</strong>
                        {{ formatDate(examForm.date) }} |
                        <strong>{{ $t('exams.level') }}:</strong>
                        {{ getDifficultyText(examForm.difficulty_level) }}
                      </div>
                    </div>
                  </div>

                  <!-- Student Information Section -->
                  <div class="student-info mb-4 p-3 border rounded bg-light">
                    <div class="row">
                      <div class="col-md-6 mb-2 mb-md-0">
                        <div class="student-info-item">
                          <strong>{{ $t('exams.student_name') }}:</strong> _________________________
                        </div>
                      </div>
                      <div class="col-md-6">
                        <div class="student-info-item">
                          <strong>{{ $t('exams.class_label') }}:</strong> _________________________
                        </div>
                      </div>
                    </div>
                    <div class="row mt-2">
                   
                      <div class="col-md-6">
                        <div class="student-info-item">
                          <strong>{{ $t('exams.signature') }}:</strong> _________________
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Exam Content -->
                  <div class="content">
                    <div
                      v-for="(item, index) in examStructure"
                      :key="index"
                      class="mb-3"
                    >
                      <!-- Title Item -->
                      <div
                        v-if="item?.type === 'title'"
                        class="section-title h5 text-primary border-bottom pb-2 mb-3"
                      >
                        {{ item?.title_name || $t('exams.section_title') }}
                      </div>

                      <!-- Question Item -->
                      <div v-else-if="item?.type === 'question'" class="question">
                        <div class="question-container d-flex">
                          <div class="question-number pe-2 pe-md-3 fw-bold text-primary">
                            {{ getQuestionNumber(index) }}.
                          </div>
                          <div class="question-content flex-grow-1">
                            <div class="question-text mb-2">
                              {{ getQuestionText(item.question_id) }}
                            </div>

                            <!-- Question Image -->
                            <div
                              v-if="getQuestionImage(item.question_id)"
                              class="question-image text-center mb-2"
                            >
                              <img
                                :src="getQuestionImage(item.question_id)"
                                :alt="$t('exams.question')"
                                class="img-fluid"
                                style="max-height: 200px"
                              />
                            </div>

                            <!-- Multiple Choice Options -->
                            <div
                              v-if="getQuestionType(item.question_id) === 'multiple_choice'"
                              class="options"
                            >
                              <div
                                v-for="(answer, ansIndex) in getQuestionAnswers(item.question_id)"
                                :key="ansIndex"
                                class="option d-flex align-items-center mb-1"
                              >
                                <span class="option-circle me-2"></span>
                                <span class="option-label fw-bold me-1">
                                  {{ getAnswerLabel(ansIndex) }})
                                </span>
                                <span>{{ answer?.answer || '' }}</span>
                              </div>
                            </div>

                            <!-- True/False Options -->
                            <div
                              v-else-if="getQuestionType(item.question_id) === 'true_false'"
                              class="options"
                            >
                              <div class="option d-flex align-items-center mb-1">
                                <span class="option-circle me-2"></span>
                                <span class="option-label fw-bold me-1">أ)</span>
                                <span>{{ $t('exams.true') }}</span>
                              </div>
                              <div class="option d-flex align-items-center mb-1">
                                <span class="option-circle me-2"></span>
                                <span class="option-label fw-bold me-1">ب)</span>
                                <span>{{ $t('exams.false') }}</span>
                              </div>
                            </div>

                            <!-- Short Answer Line -->
                            <div
                              v-else-if="getQuestionType(item.question_id) === 'short_answer'"
                              class="answer-space mt-3"
                            >
                              <div class="answer-line border-bottom" style="height: 2px"></div>
                            </div>

                            <!-- Essay Answer Lines -->
                            <div
                              v-else-if="getQuestionType(item.question_id) === 'essay'"
                              class="answer-space mt-3"
                            >
                              <div class="essay-lines">
                                <div
                                  class="answer-line border-bottom mb-2"
                                  style="height: 2px"
                                  v-for="line in 5"
                                  :key="line"
                                />
                              </div>
                            </div>

                            <!-- Matching Instructions -->
                            <div
                              v-else-if="getQuestionType(item.question_id) === 'matching'"
                              class="matching-instructions mt-3"
                            >
                              <p class="fw-bold mb-2">
                                {{ $t('exams.matching_instructions') }}
                              </p>
                              <div class="row">
                                <div class="col-md-6">
                                  <div
                                    v-for="(answer, ansIndex) in getQuestionAnswers(item.question_id)"
                                    :key="ansIndex"
                                    class="mb-2"
                                  >
                                    <span class="fw-bold me-1">{{ ansIndex + 1 }}.</span>
                                    {{ answer?.question_part || '' }}
                                  </div>
                                </div>
                                <div class="col-md-6">
                                  <div
                                    v-for="(answer, ansIndex) in getQuestionAnswers(item.question_id)"
                                    :key="ansIndex"
                                    class="mb-2"
                                  >
                                    <span class="fw-bold me-1">{{ getAnswerLabel(ansIndex) }})</span>
                                    {{ answer?.answer || '' }}
                                  </div>
                                </div>
                              </div>
                            </div>
                            <!-- /Matching -->
                          </div>
                        </div>
                      </div>
                      <!-- /Question -->
                    </div>
                  </div>

                  <!-- Exam Footer (NOW OUTSIDE THE v-for, ALWAYS VISIBLE IF SET) -->
                  <div
                    v-if="examForm?.end_title"
                    class="footer text-center mt-4 pt-3 border-top text-muted"
                  >
                    {{ examForm.end_title }}
                  </div>
                </div>

                <!-- Answer Key Preview -->
                <div
                  v-else
                  class="answer-key professional-answer-key p-3 p-md-4"
                >
                  <div class="header text-center mb-3 mb-md-4">
                    <div class="title h5 text-success mb-1">
                      {{ $t('exams.answer_key') }}
                    </div>
                    <div class="exam-title h6 text-muted">
                      {{ examForm?.title || $t('exams.title_placeholder') }}
                    </div>
                  </div>

                  <div class="table-responsive">
                    <table class="table table-bordered table-hover">
                      <thead class="table-success">
                        <tr>
                          <th style="width: 10%" class="text-center">#</th>
                          <th style="width: 50%">{{ $t('exams.question') }}</th>
                          <th style="width: 15%" class="text-center">
                            {{ $t('exams.type') }}
                          </th>
                          <th style="width: 25%" class="text-center">
                            {{ $t('exams.correct_answer') }}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="(item, index) in questionStructureItems"
                          :key="index"
                        >
                          <td class="text-center fw-bold">
                            {{ getQuestionNumber(examStructure.indexOf(item)) }}
                          </td>
                          <td class="small">
                            {{ truncateText(getQuestionText(item?.question_id), 80) }}
                          </td>
                          <td class="text-center">
                            <span
                              class="badge"
                              :class="getQuestionTypeClass(getQuestionType(item?.question_id))"
                            >
                              {{ getQuestionTypeDisplayName(getQuestionType(item?.question_id)) }}
                            </span>
                          </td>
                          <td class="correct-answer small">
                            <!-- Multiple Choice -->
                            <div
                              v-if="getQuestionType(item?.question_id) === 'multiple_choice'"
                            >
                              <span
                                v-for="(answer, ansIndex) in getCorrectAnswers(item.question_id)"
                                :key="ansIndex"
                                class="badge bg-success me-1 mb-1"
                              >
                                {{
                                  getAnswerLabel(
                                    getQuestionAnswers(item.question_id).findIndex(
                                      (a) => a.id === answer.id
                                    )
                                  )
                                }}
                              </span>
                            </div>

                            <!-- True/False -->
                            <div
                              v-else-if="getQuestionType(item?.question_id) === 'true_false'"
                            >
                              <span class="badge bg-success">
                                {{ getTrueFalseAnswer(item.question_id) }}
                              </span>
                            </div>

                            <!-- Text -->
                            <div
                              v-else-if="
                                getQuestionType(item?.question_id) === 'short_answer' ||
                                getQuestionType(item?.question_id) === 'essay'
                              "
                            >
                              {{ truncateText(getTextAnswer(item.question_id), 50) }}
                            </div>

                            <!-- Matching -->
                            <div
                              v-else-if="getQuestionType(item?.question_id) === 'matching'"
                            >
                              <div class="d-flex flex-wrap gap-1">
                                <div
                                  v-for="(answer, ansIndex) in getQuestionAnswers(item.question_id)"
                                  :key="ansIndex"
                                  class="small"
                                >
                                  {{ ansIndex + 1 }}-{{ getAnswerLabel(ansIndex) }}
                                </div>
                              </div>
                            </div>

                            <!-- No Answer -->
                            <div v-else class="text-danger small">
                              <i class="fas fa-exclamation-circle me-1"></i>
                              {{ $t('exams.not_specified') }}
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                <!-- /Answer Key -->
              </div>
            </div>
          </div>
        </div>
        <!-- /Right Panel -->
      </div>
      <!-- /Main Content -->
    </div>
    <!-- /Create Exam View -->

    <!-- Saved Exams View -->
    <div v-if="currentView === 'saved'">
      <div class="row mb-3">
        <div class="col-12">
          <div class="card shadow-sm">
            <div class="card-body py-2">
              <div class="row g-2">
                <!-- Search Filter -->
                <div class="col-md-4">
                  <input
                    type="text"
                    class="form-control form-control-sm"
                    :placeholder="$t('exams.search_exams')"
                    v-model="savedExamsSearchFilter"
                  />
                </div>
                <!-- Class Filter -->
                <div class="col-md-4">
                  <select
                    class="form-select form-select-sm"
                    v-model="savedExamsClassFilter"
                  >
                    <option value="">{{ $t('exams.all_classes') }}</option>
                    <option
                      v-for="classItem in isTeacher ? teacherClasses : classes"
                      :key="classItem.id"
                      :value="classItem.id"
                    >
                      {{ classItem.name }}
                    </option>
                  </select>
                </div>
                <!-- Subject Filter -->
                <div class="col-md-4">
                  <select
                    class="form-select form-select-sm"
                    v-model="savedExamsSubjectFilter"
                    :disabled="!savedExamsClassFilter"
                  >
                    <option value="">{{ $t('exams.all_subjects') }}</option>
                    <option
                      v-for="subject in savedExamsClassFilter
                        ? (isTeacher ? teacherSubjects : subjects.filter(s => s.class_id == savedExamsClassFilter))
                        : []"
                      :key="subject.id"
                      :value="subject.id"
                    >
                      {{ subject.name }}
                    </option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div> <!-- /Filters -->

      <div class="row">
        <!-- Saved Exams List -->
        <div class="col-xl-4 col-lg-5 col-md-12 mb-3 mb-md-0">
          <div class="card h-100 shadow-sm">
            <div class="bg-white m-4">
              <h6 class="mb-0">
                <i class="fas fa-folder me-1 me-md-2"></i>
                {{ $t('exams.saved_exams') }}
              </h6>
            </div>
            <div class="card-body p-0">
              <div class="saved-exams-list" style="max-height: 600px; overflow-y: auto">
                <div v-if="savedExams.length === 0" class="text-center py-4 text-muted">
                  <i class="fas fa-folder-open fa-3x mb-3"></i>
                  <p class="mb-2">{{ $t('exams.no_saved_exams') }}</p>
                  <button class="btn btn-primary btn-sm mt-2" @click="currentView = 'create'" v-can="'exam_forms.create'">
                    {{ $t('exams.new_exam') }}
                  </button>
                </div>

                <div
                  v-for="exam in filteredSavedExams"
                  :key="exam.id"
                  class="saved-exam-card p-3 border-bottom cursor-pointer"
                  :class="{ 'bg-light-primary': selectedSavedExam?.id === exam.id }"
                  @click="selectSavedExam(exam)"
                >
                  <div class="d-flex justify-content-between align-items-start">
                    <div class="flex-grow-1 text-start">
                      <h6 class="mb-1 text-primary">{{ exam.title }}</h6>
                      <div class="small text-muted mb-2">
                        {{ exam.subject_name || getSubjectName(exam.subject_id) || $t('exams.not_specified') }}
                        <div>
                          <strong>{{ $t('exams.num_questions') }}:</strong>
                          {{
                            exam.structure?.filter((item) => item.type === 'question').length
                          }}
                        </div>
                      </div>
                      <div class="small text-muted">
                        <i class="fas fa-calendar me-1"></i>
                        {{ formatDate(exam.created_at) }}
                      </div>
                    </div>

                    <div class="d-flex flex-column gap-1 ms-2">
                      <button
                        class="btn btn-sm text-secondary"
                        @click.stop="printSavedExam(exam)"
                        :title="$t('exams.print_exam')"
                      >
                        <i class="fas fa-print"></i>
                      </button>
                      <button
                        class="btn btn-sm text-primary"
                        @click.stop="exportSavedExamToPDF(exam)"
                        :title="$t('exams.download_pdf')"
                      >
                        <i class="fas fa-download"></i>
                      </button>
                      <button
                        class="btn btn-sm text-danger"
                        @click.stop="openDeleteDialog(exam)"
                        :title="$t('exams.delete')"
                        v-can="'exam_forms.delete'"
                      >
                        <i class="fas fa-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>
                <!-- /saved item -->
              </div>
            </div>
          </div>
        </div>

        <!-- Saved Exam Preview -->
        <div class="col-xl-8 col-lg-7 col-md-12">
          <div class="card h-100">
            <div class="bg-white d-flex justify-content-between align-items-center m-4">
              <h6 class="mb-0">
                <i class="fas fa-eye me-1 me-md-2"></i>
                {{
                  selectedSavedExam
                    ? $t('exams.preview_exam')
                    : $t('exams.select_exam_for_preview')
                }}
              </h6>
              <div v-if="selectedSavedExam" class="d-flex gap-2">
                <button
                  class="btn btn-sm"
                  :class="savedExamPreviewMode === 'exam' ? 'btn-primary' : 'btn-outline-primary'"
                  @click="savedExamPreviewMode = 'exam'"
                >
                  {{ $t('exams.exam_sheet') }}
                </button>
                <button
                  class="btn btn-sm"
                  :class="savedExamPreviewMode === 'answers' ? 'btn-primary' : 'btn-outline-primary'"
                  @click="savedExamPreviewMode = 'answers'"
                >
                  {{ $t('exams.answer_key') }}
                </button>
              </div>
            </div>

            <div class="card-body p-0">
              <div v-if="!selectedSavedExam" class="text-center py-5 text-muted">
                <i class="fas fa-mouse-pointer fa-3x mb-3"></i>
                <p class="mb-0">{{ $t('exams.choose_exam_preview') }}</p>
              </div>

              <div
                v-else
                class="saved-paper-container"
                ref="savedPaperContainer"
                style="height: 600px; overflow-y: auto"
              >
                <!-- Saved Exam Paper Preview -->
                <div v-if="savedExamPreviewMode === 'exam'" class="exam-paper p-3 p-md-4">
                  <!-- Student Info Section -->
                  <div class="student-info-section mb-4 p-3 border rounded bg-light">
                    <div class="row">
                      <div class="col-md-6 mb-2 mb-md-0">
                        <div class="mb-2">
                          <span class="fw-bold">{{ $t('exams.student_name') }}: </span>
                          <span class="student-line">_________________________</span>
                        </div>
                        <div class="mb-2">
                          <span class="fw-bold">{{ $t('exams.student_number') }}: </span>
                          <span class="student-line">_________________</span>
                        </div>
                      </div>
                      <div class="col-md-6">
                        <div class="mb-2">
                          <span class="fw-bold">{{ $t('exams.signature') }}: </span>
                          <span class="student-line">_________________</span>
                        </div>
                        <div class="mb-2">
                          <span class="fw-bold">{{ $t('exams.date') }}: </span>
                          <span class="student-line">_________________</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Header -->
                  <div class="exam-header text-center mb-4 pb-3 border-bottom">
                    <h3 class="exam-title h4 mb-2">
                      {{ selectedSavedExam.title }}
                    </h3>
                    <div class="exam-meta row small text-muted">
                      <div class="col-md-6 text-md-end mb-1 mb-md-0">
                        <div>
                          <strong>{{ $t('exams.subject') }}:</strong>
                          {{
                            selectedSavedExam.subject_id
                              ? getSubjectName(selectedSavedExam.subject_id)
                              : $t('exams.not_specified')
                          }}
                        </div>
                        <div>
                          <strong>{{ $t('exams.teacher') }}:</strong>
                          {{
                            selectedSavedExam.teacher_id
                              ? getTeacherName(selectedSavedExam.teacher_id)
                              : $t('exams.not_specified')
                          }}
                        </div>
                      </div>
                      <div class="col-md-6 text-md-start">
                        <div>
                          <strong>{{ $t('exams.date') }}:</strong>
                          {{ formatDate(selectedSavedExam.date) }}
                        </div>
                        <div>
                          <strong>{{ $t('exams.level') }}:</strong>
                          {{ getDifficultyText(selectedSavedExam.difficulty_level) }}
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Content -->
                  <div class="exam-content">
                    <div
                      v-for="(item, index) in selectedSavedExam.structure"
                      :key="index"
                      class="question-block mb-4"
                    >
                      <!-- Title Item -->
                      <div v-if="item?.type === 'title'" class="exam-section">
                        <h4 class="section-title h5 text-primary border-bottom pb-2 mb-3">
                          {{ item?.title_name || $t('exams.section_title') }}
                        </h4>
                      </div>

                      <!-- Question Item -->
                      <div v-else-if="item?.type === 'question'" class="exam-question">
                        <div class="question-container d-flex">
                          <div class="question-number pe-2 pe-md-3 fw-bold text-primary">
                            {{ getSavedQuestionNumber(index) }}.
                          </div>
                          <div class="question-content flex-grow-1">
                            <div class="question-text mb-2">
                              {{ getSavedQuestionText(item.question_id) }}
                            </div>

                            <!-- Multiple Choice -->
                            <div
                              v-if="getSavedQuestionType(item.question_id) === 'multiple_choice'"
                              class="question-answers"
                            >
                              <div
                                v-for="(answer, ansIndex) in getSavedQuestionAnswers(item.question_id)"
                                :key="ansIndex"
                                class="answer-option mb-1 d-flex align-items-center"
                              >
                                <div class="answer-marker me-2">
                                  <div class="answer-circle"></div>
                                </div>
                                <div class="answer-text">
                                  <span class="answer-label fw-bold me-1">
                                    {{ getAnswerLabel(ansIndex) }})
                                  </span>
                                  {{ answer?.answer || '' }}
                                </div>
                              </div>
                            </div>

                            <!-- True/False -->
                            <div
                              v-else-if="getSavedQuestionType(item.question_id) === 'true_false'"
                              class="question-answers"
                            >
                              <div class="answer-option mb-1 d-flex align-items-center">
                                <div class="answer-marker me-2">
                                  <div class="answer-circle"></div>
                                </div>
                                <div class="answer-text">
                                  <span class="answer-label fw-bold me-1">أ)</span>
                                  {{ $t('exams.true') }}
                                </div>
                              </div>
                              <div class="answer-option mb-1 d-flex align-items-center">
                                <div class="answer-marker me-2">
                                  <div class="answer-circle"></div>
                                </div>
                                <div class="answer-text">
                                  <span class="answer-label fw-bold me-1">ب)</span>
                                  {{ $t('exams.false') }}
                                </div>
                              </div>
                            </div>

                            <!-- Short Answer -->
                            <div
                              v-else-if="getSavedQuestionType(item.question_id) === 'short_answer'"
                              class="answer-line mt-3"
                            >
                              <div>
                                <span class="text-muted small">{{ $t('exams.answer') }}:</span>
                                <div class="answer-line-border mt-1" style="height: 2px"></div>
                              </div>
                            </div>

                            <!-- Essay -->
                            <div
                              v-else-if="getSavedQuestionType(item.question_id) === 'essay'"
                              class="essay-answer mt-3"
                            >
                              <div>
                                <span class="text-muted fw-bold small">{{ $t('exams.essay') }}:</span>
                                <div class="essay-lines mt-1">
                                  <div
                                    class="answer-line-border mb-1"
                                    style="height: 2px"
                                    v-for="line in 4"
                                    :key="line"
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <!-- /Question -->
                    </div>
                  </div>

                  <!-- Footer -->
                  <div
                    v-if="selectedSavedExam?.end_title"
                    class="exam-footer text-center mt-4 pt-3 border-top"
                  >
                    <p class="text-muted mb-0">
                      {{ selectedSavedExam.end_title }}
                    </p>
                  </div>
                </div>

                <!-- Saved Answer Key Preview -->
                <div v-else class="answer-key p-3 p-md-4">
                  <div class="answer-header text-center mb-3 mb-md-4 pb-2 pb-md-3 border-bottom">
                    <h3 class="h5 mb-1 mb-md-2 text-success">{{ $t('exams.answer_key') }}</h3>
                    <h5 class="text-muted small">
                      {{ selectedSavedExam.title }}
                    </h5>
                  </div>

                  <div class="answer-content">
                    <div class="table-responsive">
                      <table class="table table-bordered table-hover mb-0">
                        <thead class="table-success">
                          <tr>
                            <th width="40" class="text-center">#</th>
                            <th>{{ $t('exams.question') }}</th>
                            <th width="80" class="text-center d-none d-sm-table-cell">
                              {{ $t('exams.type') }}
                            </th>
                            <th width="120" class="text-center">{{ $t('exams.answer') }}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="(item, index) in savedQuestionStructureItems"
                            :key="index"
                          >
                            <td class="text-center fw-bold">
                              {{ getSavedQuestionNumber(selectedSavedExam.examStructure.indexOf(item)) }}
                            </td>
                            <td class="small">
                              {{ truncateText(getSavedQuestionText(item?.question_id), 40) }}
                            </td>
                            <td class="text-center d-none d-sm-table-cell">
                              <span
                                class="badge"
                                :class="getQuestionTypeClass(getSavedQuestionType(item?.question_id))"
                              >
                                {{ getQuestionTypeDisplayName(getSavedQuestionType(item?.question_id)) }}
                              </span>
                            </td>
                            <td class="text-center small">
                              <!-- Multiple Choice -->
                              <div v-if="getSavedQuestionType(item?.question_id) === 'multiple_choice'">
                                <span
                                  v-for="(answer, ansIndex) in getSavedCorrectAnswers(item.question_id)"
                                  :key="ansIndex"
                                  class="badge bg-success me-1 mb-1"
                                >
                                  {{
                                    getAnswerLabel(
                                      getSavedQuestionAnswers(item.question_id).findIndex(
                                        (a) => a.id === answer.id
                                      )
                                    )
                                  }}
                                </span>
                              </div>

                              <!-- True/False -->
                              <div v-else-if="getSavedQuestionType(item?.question_id) === 'true_false'">
                                <span class="badge bg-success">
                                  {{ getSavedTrueFalseAnswer(item.question_id) }}
                                </span>
                              </div>

                              <!-- Short/Eassy -->
                              <div
                                v-else-if="
                                  getSavedQuestionType(item?.question_id) === 'short_answer' ||
                                  getSavedQuestionType(item?.question_id) === 'essay'
                                "
                              >
                                <span class="text-success small">
                                  {{ truncateText(getSavedTextAnswer(item.question_id), 30) }}
                                </span>
                              </div>

                              <div v-else class="text-warning small">
                                <i class="fas fa-exclamation-triangle me-1"></i>
                                {{ $t('exams.not_specified') }}
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div> <!-- /table -->
                  </div>
                </div>
                <!-- /Saved Answer Key -->
              </div>
            </div>
          </div>
        </div>
        <!-- /Saved Exam Preview -->
      </div>
    </div>
    <!-- /Saved Exams View -->

    <!-- Question Selection Modal -->
    <div
      class="modal fade"
      id="questionModal"
      tabindex="-1"
      aria-labelledby="questionModalLabel"
      aria-hidden="true"
    >
      <div class="modal-dialog modal-xl">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="questionModalLabel">
              {{ $t('exams.choose_questions') }}
              <span v-if="selectedQuestions.length > 0" class="badge bg-primary ms-2">
                {{ $t('exams.questions_selected', { count: selectedQuestions.length }) }}
              </span>
            </h5>
            <button
              type="button"
              class="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            />
          </div>

          <div class="modal-body">
            <!-- Search and Filters -->
            <div class="row g-2 mb-3">
              <div class="col-md-6">
                <input
                  type="text"
                  class="form-control"
                  :placeholder="$t('exams.search_questions')"
                  v-model="modalQuestionSearch"
                />
              </div>
              <div class="col-md-3">
                <select class="form-select" v-model="modalSelectedQuestionType">
                  <option value="">{{ $t('exams.all_types') }}</option>
                  <option
                    v-for="type in questionTypes"
                    :key="type.id"
                    :value="type.name"
                  >
                    {{ type.title }}
                  </option>
                </select>
              </div>
              <div class="col-md-3">
                <select class="form-select" v-model="modalSelectedQuestionLevel">
                  <option value="">{{ $t('exams.all_levels') }}</option>
                  <option
                    v-for="level in questionLevels"
                    :key="level.id"
                    :value="level.name"
                  >
                    {{ level.name }}
                  </option>
                </select>
              </div>
            </div>

            <!-- Selection Actions -->
            <div
              class="d-flex flex-column flex-sm-row justify-content-between align-items-center mb-3 gap-2"
            >
              <div class="d-flex flex-wrap gap-2">
                <button
                  class="btn btn-sm btn-outline-secondary"
                  @click="clearSelectedQuestions"
                  :disabled="selectedQuestions.length === 0"
                >
                  <i class="fas fa-times me-1"></i>
                  {{ $t('exams.clear_selection') }}
                </button>
                <button
                  class="btn btn-sm btn-outline-primary"
                  @click="selectAllFilteredQuestions"
                  :disabled="modalFilteredQuestions.length === 0"
                >
                  <i class="fas fa-check-double me-1"></i>
                  {{ $t('exams.select_all') }}
                </button>
              </div>
              <small class="text-muted">
                {{ $t('exams.available_questions', { count: modalFilteredQuestions.length }) }}
              </small>
            </div>

            <!-- Questions Grid -->
            <div class="questions-grid" style="max-height: 400px; overflow-y: auto">
              <div
                v-for="question in modalFilteredQuestions"
                :key="question.id"
                class="question-modal-card mb-3 p-3 border rounded cursor-pointer"
                :class="{
                  'border-primary bg-light': selectedQuestions.includes(question.id),
                  'border-warning': isQuestionUsedInStructure(question.id) && !selectedQuestions.includes(question.id)
                }"
                @click="toggleQuestionSelection(question.id)"
              >
                <div class="d-flex justify-content-between align-items-start">
                  <div class="flex-grow-1">
                    <div class="d-flex flex-wrap align-items-center mb-2 gap-1">
                      <span class="badge" :class="getQuestionTypeClass(question.question_type?.name)">
                        {{ getQuestionTypeName(question.question_type?.id) }}
                      </span>
                      <span class="badge" :class="getQuestionLevelClass(question.question_level?.name)">
                        {{ getQuestionLevelName(question.question_level?.id) }}
                      </span>
                      <span
                        v-if="isQuestionUsedInStructure(question.id)"
                        class="badge bg-warning text-dark"
                      >
                        {{ $t('exams.already_used') }}
                      </span>
                      <i
                        v-if="selectedQuestions.includes(question.id)"
                        class="fas fa-check-circle text-primary"
                      />
                    </div>

                    <p class="mb-2 small">
                      {{ truncateText(question?.description || '', 120) }}
                    </p>

                    <div v-if="question?.answers?.length > 0" class="small text-muted">
                      <strong>{{ $t('exams.num_options') }}:</strong>
                      {{ question.answers.length }}
                    </div>
                  </div>

                  <div class="ms-2">
                    <i
                      :class="selectedQuestions.includes(question.id) ? 'fas fa-check text-primary' : 'fas fa-plus text-secondary'"
                    ></i>
                  </div>
                </div>
              </div>

              <div v-if="modalFilteredQuestions.length === 0" class="text-center py-4 text-muted">
                <i class="fas fa-search fa-2x mb-2"></i>
                <p class="mb-0">{{ $t('exams.no_questions_found') }}</p>
              </div>
            </div>
          </div>

          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-sm btn-outline-secondary"
              data-bs-dismiss="modal"
            >
              {{ $t('exams.modal_cancel') }}
            </button>
            <button
              type="button"
              class="btn btn-primary btn-sm"
              @click="addSelectedQuestions"
              :disabled="selectedQuestions.length === 0"
            >
              <i class="fas fa-plus me-1"></i>
              {{ $t('exams.add_selected_questions', { count: selectedQuestions.length }) }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Dialog -->
    <DeleteConfirmationDialog
      ref="deleteDialog"
      entity-name="Exam"
      :entity-label="$t('exams.exam_sheet')"
      @delete="confirmDeleteExam"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useToast } from "vue-toastification";
import html2canvas from "html2canvas";
import DeleteConfirmationDialog from "@/components/common/DeleteDialog.vue";
import { useStore } from "vuex";
import jsPDF from "jspdf";
import api from "@/api";
import { useI18n } from "vue-i18n";

const { t } = useI18n();
const toast = useToast();
const store = useStore();

/* ----------------------- Refs / State ----------------------- */
const currentView = ref("create"); // "create" | "saved"
const currentStep = ref(0);
const previewMode = ref("exam"); // "exam" | "answers"
const savedExamPreviewMode = ref("exam");
const isSaving = ref(false);
const errors = ref({});

const paperContainer = ref(null);
const savedPaperContainer = ref(null);
const deleteDialog = ref(null);
const draggingIndex = ref(null);
const hoverIndex = ref(null);

const onDragStart = (e, index) => {
  draggingIndex.value = index;
  e.dataTransfer.effectAllowed = "move";
  e.dataTransfer.setData("text/plain", String(index));
};

const onDragOver = (e, index) => {
  e.preventDefault();
  e.dataTransfer.dropEffect = "move";
  hoverIndex.value = index;
};

const onDrop = (e, index) => {
  e.preventDefault();
  const from =
    draggingIndex.value !== null
      ? draggingIndex.value
      : parseInt(e.dataTransfer.getData("text/plain"), 10);

  const to = index;
  if (Number.isNaN(from) || from === to) {
    draggingIndex.value = null;
    hoverIndex.value = null;
    return;
  }

  const moved = examStructure.value.splice(from, 1)[0];
  examStructure.value.splice(to, 0, moved);

  draggingIndex.value = null;
  hoverIndex.value = null;
};

const onDragEnd = () => {
  draggingIndex.value = null;
  hoverIndex.value = null;
};

// Form
const examForm = ref({
  title: "",
  class_id: "",
  subject_id: "",
  teacher_id: "",
  date: "",
  difficulty_level: "",
  end_title: "",
});

// Builder data
const examStructure = ref([]); // [{ type: 'title'|'question', title_name?, question_id? }]
const availableQuestions = ref([]);

// Catalogs
const subjects = ref([]);
const teachers = ref([]);
const questionTypes = ref([]);
const questionLevels = ref([]);
const classes = ref([]);

// Filters (left panel)
const questionSearch = ref("");
const selectedQuestionType = ref("");
const selectedQuestionLevel = ref("");

// Modal (step 2 add questions modal)
const modalQuestionSearch = ref("");
const modalSelectedQuestionType = ref("");
const modalSelectedQuestionLevel = ref("");
const selectedQuestions = ref([]);

// Saved exams
const savedExams = ref([]);
const selectedSavedExam = ref(null);

// Saved exams filters (right tab)
const savedExamsClassFilter = ref("");
const savedExamsSubjectFilter = ref("");
const savedExamsSearchFilter = ref("");

// User / Role
const currentUser = computed(() => store.state.user);
const isTeacher = computed(() => currentUser.value?.role?.slug === "teacher");
const teacherClasses = ref([]);
const teacherSubjects = ref([]);

/* ----------------------- Constants ----------------------- */
const steps = [
  { title: t("exams.step_settings") || "إعدادات النموذج", icon: "fas fa-file-alt" },
  { title: t("exams.step_structure") || "هيكل الامتحان", icon: "fas fa-sitemap" },
  { title: t("exams.step_questions") || "إدارة الأسئلة", icon: "fas fa-question-circle" },
  { title: t("exams.step_preview") || "معاينة وحفظ", icon: "fas fa-eye" },
];

/* ----------------------- Computeds ----------------------- */
const canProceed = computed(() => {
  switch (currentStep.value) {
    case 0:
      return (
        !!examForm.value.title &&
        !!examForm.value.class_id &&
        !!examForm.value.subject_id &&
        !!examForm.value.date &&
        !!examForm.value.difficulty_level
      );
    case 1:
      return (
        examStructure.value.length > 0 &&
        examStructure.value.some((i) => i.type === "question" && i.question_id)
      );
    default:
      return true;
  }
});

const filteredQuestions = computed(() =>
  (availableQuestions.value || []).filter((q) => {
    const matchesSearch =
      !questionSearch.value ||
      q?.description?.toLowerCase().includes(questionSearch.value.toLowerCase());

    const matchesType =
      !selectedQuestionType.value || q?.question_type?.name === selectedQuestionType.value;

    const matchesLevel =
      !selectedQuestionLevel.value || q?.question_level?.name === selectedQuestionLevel.value;

    return matchesSearch && matchesType && matchesLevel;
  })
);

const modalFilteredQuestions = computed(() =>
  (availableQuestions.value || []).filter((q) => {
    const matchesSearch =
      !modalQuestionSearch.value ||
      q?.description?.toLowerCase().includes(modalQuestionSearch.value.toLowerCase());

    const matchesType =
      !modalSelectedQuestionType.value || q?.question_type?.name === modalSelectedQuestionType.value;

    const matchesLevel =
      !modalSelectedQuestionLevel.value || q?.question_level?.name === modalSelectedQuestionLevel.value;

    return matchesSearch && matchesType && matchesLevel;
  })
);

const questionStructureItems = computed(() =>
  examStructure.value.filter((i) => i?.type === "question")
);

const savedQuestionStructureItems = computed(() => {
  if (!selectedSavedExam.value) return [];
  return (selectedSavedExam.value.examStructure || []).filter((i) => i?.type === "question");
});

const filteredSavedExams = computed(() =>
  (savedExams.value || []).filter((exam) => {
    const matchesClass = !savedExamsClassFilter.value || exam.class_id == savedExamsClassFilter.value;
    const matchesSubject =
      !savedExamsSubjectFilter.value || exam.subject_id == savedExamsSubjectFilter.value;
    const term = (savedExamsSearchFilter.value || "").toLowerCase();
    const matchesSearch =
      !term ||
      exam.title?.toLowerCase().includes(term) ||
      exam.subject_name?.toLowerCase().includes(term);
    return matchesClass && matchesSubject && matchesSearch;
  })
);

/* ----------------------- API: Fetchers ----------------------- */
const fetchInitialData = async () => {
  try {
    const [subjectsRes, teachersRes, typesRes, levelsRes, classesRes] = await Promise.all([
      api.get("/subjects"),
      api.get("/teachers"),
      api.get("/question-types"),
      api.get("/question-levels"),
 api.get("/classes", { params: { all: true } }),     ]);

    subjects.value = subjectsRes.data?.data || [];
    teachers.value = teachersRes.data?.data || [];
    questionTypes.value = typesRes.data?.data || [];
    questionLevels.value = levelsRes.data?.data || [];
    classes.value = classesRes.data?.data || [];
  } catch (e) {
    console.error("Error fetching initial data:", e);
    toast.error(t("exams.initial_data_error") || "فشل في تحميل البيانات الأولية");
  }
};

const fetchTeacherData = async () => {
  if (!isTeacher.value || !currentUser.value?.id) return;

  // Link the teacher in the form early
  examForm.value.teacher_id = currentUser.value.id;

  try {
    const classesRes = await api.get(`/teachers/${currentUser.value.id}/classes`);
    teacherClasses.value = classesRes.data?.data || [];

    // If only one class, auto-select it and fetch subjects
    if (teacherClasses.value.length === 1) {
      examForm.value.class_id = teacherClasses.value[0].id;
      await fetchTeacherSubjects();
    }
  } catch (e) {
    console.error("Error fetching teacher data:", e);
    toast.error(t("exams.teacher_data_error") || "فشل في تحميل بيانات المعلم");
  }
};

const fetchTeacherSubjects = async () => {
  if (!isTeacher.value || !examForm.value.class_id) return;

  try {
    const res = await api.get(`/teachers/${currentUser.value.id}/subjects`, {
      params: { class_id: examForm.value.class_id },
    });
    teacherSubjects.value = res.data?.data || [];

    // If only one subject, auto-select and load questions
    if (teacherSubjects.value.length === 1) {
      examForm.value.subject_id = teacherSubjects.value[0].id;
      examForm.value.teacher_id = currentUser.value.id;
      await fetchQuestionsBySubject();
    }
  } catch (e) {
    console.error("Error fetching teacher subjects:", e);
    toast.error(t("exams.teacher_subjects_error") || "فشل في تحميل مواد المعلم");
  }
};

const fetchClassSubjects = async () => {
  if (!examForm.value.class_id) {
    subjects.value = [];
    return;
  }

  try {
    const res = await api.get(
      `/classes/${examForm.value.class_id}/subjects?all=true`
    );

    subjects.value = (res.data?.data || []).map((item) => ({
      id: item.subject_id,
      name: item.subject?.name,
      code: item.subject?.code,
    }));
  } catch (e) {
    console.error("Error fetching class subjects:", e);
    subjects.value = [];
  }
};

const fetchQuestionsBySubject = async () => {
  if (!examForm.value.subject_id || !examForm.value.class_id) {
    availableQuestions.value = [];
    return;
  }

  try {
    const params = {
      subject_id: examForm.value.subject_id,
      class_id: examForm.value.class_id,
      ...(isTeacher.value && currentUser.value?.id ? { teacher_id: currentUser.value.id } : {}),
    };
    const res = await api.get("/questions", { params });
    availableQuestions.value = res.data?.data || [];

    // If no questions, remove existing question items from structure to avoid dangling references
    if (availableQuestions.value.length === 0) {
      examStructure.value = examStructure.value.filter((item) => item.type !== "question");
      toast.info(t("exams.no_questions_available") || "لا توجد أسئلة متاحة");
    }
  } catch (e) {
    console.error("Error fetching questions:", e);
    toast.error(t("exams.failed_loading_questions") || "فشل في تحميل الأسئلة");
  }
};

const fetchSavedExams = async () => {
  try {
    const res = await api.get("/exams", { params: { include: "questions" } });
    savedExams.value = (res.data?.data || []).map((exam) => ({
      ...exam,
      questions: exam.questions || [],
    }));
    if (currentView.value === "saved") selectFirstSavedExam();
  } catch (e) {
    console.error("Error loading exams:", e);
    toast.error(t("exams.load_exams_error") || "Failed to load saved exams");
    savedExams.value = [];
    selectedSavedExam.value = null;
  }
};
/* ----------------------- Actions: Save / Delete ----------------------- */
const saveExam = async () => {
  if (!canProceed.value) {
    toast.warning(t("exams.complete_required_steps") || "Please complete all required steps");
    return;
  }

  isSaving.value = true;

  try {
    const payload = {
      title: examForm.value.title,
      end_title: examForm.value.end_title,
      class_id: examForm.value.class_id,
      subject_id: examForm.value.subject_id,
      teacher_id: examForm.value.teacher_id,
      date: examForm.value.date,
      difficulty_level: examForm.value.difficulty_level,
      structure: examStructure.value,
    };

    const createRes = await api.post("/exams", payload);
    const newId = createRes?.data?.data?.id;

    // Fetch the full exam (with relations) once
    const fullRes = await api.get(`/exams/${newId}`, { params: { include: "questions" } });
    const fullExam = fullRes?.data?.data;

    if (fullExam) {
      savedExams.value.unshift(fullExam);
    }

    toast.success(t("exams.exam_saved_successfully") || "Exam saved successfully!");
    resetExamForm();
    currentView.value = selectFirstSavedExam();

  } catch (e) {
    console.error("Error saving exam:", e);
    toast.error(e?.response?.data?.message || t("exams.failed_save_exam") || "Failed to save exam");
  } finally {
    isSaving.value = false;
  }
};

const confirmDeleteExam = async (examId) => {
  try {
    await api.delete(`/exams/${examId}`);
    savedExams.value = savedExams.value.filter((ex) => ex.id !== examId);
    if (selectedSavedExam.value?.id === examId) {
      selectedSavedExam.value = null;
    }
    toast.success(t("exams.exam_deleted_successfully") || "Exam deleted successfully");
  } catch (e) {
    console.error("Error deleting exam:", e);
    toast.error(t("exams.failed_delete_exam") || "Failed to delete exam");
  }
};

/* ----------------------- Select / Print / Export ----------------------- */
const selectSavedExam = async (exam) => {
  try {
    // Prepare object used by template (keeps structure under "examStructure")
    selectedSavedExam.value = { ...exam, examStructure: exam.structure || [] };

    // Ensure questions are loaded
    if (!Array.isArray(exam.questions) || exam.questions.length === 0) {
      const res = await api.get(`/questions`, { params: { exam_id: exam.id } });
      selectedSavedExam.value.questions = res.data?.data || [];
    } else {
      selectedSavedExam.value.questions = exam.questions;
    }

    savedExamPreviewMode.value = "exam";
  } catch (e) {
    console.error("Error loading exam questions:", e);
    toast.error(t("exams.failed_load_exam_questions") || "Failed to load exam questions");
    selectedSavedExam.value = { ...exam, examStructure: exam.structure || [], questions: [] };
  }
};

const openDeleteDialog = (exam) => {
  deleteDialog.value?.openDialog(exam.id, exam.title);
};

/* ----------------------- UI helpers: steps & nav ----------------------- */
const validateAndProceed = () => {
  errors.value = {};
  nextStep();
};
const nextStep = () => {
  if (currentStep.value < steps.length - 1) currentStep.value++;
};
const previousStep = () => {
  if (currentStep.value > 0) currentStep.value--;
};

const handleClassChange = async () => {
  examForm.value.subject_id = "";
  examForm.value.teacher_id = "";
  availableQuestions.value = [];

  if (!examForm.value.class_id) return;

  if (isTeacher.value) {
    await fetchTeacherSubjects();
  } else {
    await fetchClassSubjects();
  }
};

const handleSubjectChange = () => {
  examForm.value.teacher_id = "";
  availableQuestions.value = [];
  if (examForm.value.subject_id) fetchQuestionsBySubject();
};

const resetExamForm = () => {
  examForm.value = {
    title: "",
    class_id: "",
    subject_id: "",
    teacher_id: "",
    date: "",
    difficulty_level: "",
    end_title: "",
  };
  examStructure.value = [];
  currentStep.value = 0;
  errors.value = {};
};

/* ----------------------- Builder actions ----------------------- */
const openQuestionModal = () => {
  if (!availableQuestions.value?.length) {
    toast.warning(
      t("exams.no_questions_available_select_first") ||
        "لا توجد أسئلة متاحة. تأكد من اختيار المعلم والمادة أولاً."
    );
    return;
  }
  modalQuestionSearch.value = "";
  modalSelectedQuestionType.value = "";
  modalSelectedQuestionLevel.value = "";
  selectedQuestions.value = [];

  const el = document.getElementById("questionModal");
  if (el) {
    const modal = new window.bootstrap.Modal(el);
    modal.show();
  }
};

const toggleQuestionSelection = (qid) => {
  const i = selectedQuestions.value.indexOf(qid);
  if (i >= 0) selectedQuestions.value.splice(i, 1);
  else selectedQuestions.value.push(qid);
};

const clearSelectedQuestions = () => (selectedQuestions.value = []);

const selectAllFilteredQuestions = () => {
  const ids = modalFilteredQuestions.value.map((q) => q.id);
  selectedQuestions.value = [...new Set([...selectedQuestions.value, ...ids])];
};

const addSelectedQuestions = () => {
  let added = 0;
  selectedQuestions.value.forEach((qid) => {
    if (!isQuestionUsedInStructure(qid)) {
      examStructure.value.push({ type: "question", question_id: qid });
      added++;
    }
  });

  const el = document.getElementById("questionModal");
  if (el) {
    const modal = window.bootstrap.Modal.getInstance(el);
    modal?.hide();
  }

  if (added > 0) toast.success(t("exams.added_success", { count: added }) || `تم إضافة ${added} سؤال بنجاح`);
  const ignored = selectedQuestions.value.length - added;
  if (ignored > 0) {
    toast.warning(
      t("exams.ignored_already_used", { count: ignored }) ||
        `تم تجاهل ${ignored} سؤال لأنها مستخدمة بالفعل`
    );
  }

  selectedQuestions.value = [];
};

const addTitleToStructure = () => examStructure.value.push({ type: "title", title_name: "" });
const removeStructureItem = (idx) => examStructure.value.splice(idx, 1);

const toggleQuestionInStructure = (qid) => {
  const idx = examStructure.value.findIndex((i) => i.type === "question" && i.question_id == qid);
  if (idx >= 0) examStructure.value.splice(idx, 1);
  else examStructure.value.push({ type: "question", question_id: qid });
};

/* ----------------------- Getters: Catalog names / meta ----------------------- */
const getSubjectName = (subjectId) => {
  if (!subjectId) return t("exams.not_specified") || "غير محدد";
  const s = (subjects.value || []).find((x) => x.id == subjectId);
  return s?.name || (t("exams.not_specified") || "غير محدد");
};

const getTeacherName = (teacherId) => {
  const te = (teachers.value || []).find((x) => x.id == teacherId);
  return te?.username || (t("exams.not_specified") || "غير محدد");
};

const formatDate = (date) => {
  if (!date) return t("exams.not_specified") || "غير محدد";
  const d = new Date(date);
  return isNaN(d.getTime()) ? (t("exams.not_specified") || "غير محدد") : d.toLocaleDateString("ar");
};

const getDifficultyText = (level) => {
  switch (level) {
    case "easy":
      return t("exams.easy") || "سهل";
    case "medium":
      return t("exams.medium") || "متوسط";
    case "hard":
      return t("exams.hard") || "صعب";
    default:
      return t("exams.not_specified") || "غير محدد";
  }
};

/* ----------------------- Getters: Questions (current) ----------------------- */
const getQuestionText = (qid) => {
  const q = (availableQuestions.value || []).find((x) => x.id == qid);
  return q?.description || t("exams.no_question_text") || "نص السؤال غير متوفر";
};
const getQuestionImage = (qid) => {
  const q = (availableQuestions.value || []).find((x) => x.id == qid);
  return q?.image || null;
};
const getQuestionAnswers = (qid) => {
  const q = (availableQuestions.value || []).find((x) => x.id == qid);
  return q?.answers || [];
};
const getQuestionType = (qid) => {
  const q = (availableQuestions.value || []).find((x) => x.id == qid);
  return q?.question_type?.name || "";
};
const getCorrectAnswers = (qid) => {
  const q = (availableQuestions.value || []).find((x) => x.id == qid);
  if (!q?.answers) return [];
  return q.question_type?.name === "multiple_choice" ? q.answers.filter((a) => a.is_correct) : q.answers;
};
const getTrueFalseAnswer = (qid) => {
  const a = getQuestionAnswers(qid);
  if (!a.length) return t("exams.not_specified") || "غير محدد";
  return a[0].answer === "true" ? t("exams.true") || "صواب" : t("exams.false") || "خطأ";
};
const getTextAnswer = (qid) => {
  const a = getQuestionAnswers(qid);
  return a?.[0]?.answer || t("exams.not_specified") || "غير محدد";
};

const getQuestionNumber = (idx) => {
  let n = 1;
  for (let i = 0; i < idx; i++) if (examStructure.value[i]?.type === "question") n++;
  return n;
};

/* ----------------------- Getters: Questions (saved exam) ----------------------- */
const getSavedQuestionText = (qid) => {
  const q = selectedSavedExam.value?.questions?.find((x) => x.id == qid);
  return q?.description || t("exams.no_question_text") || "نص السؤال غير متوفر";
};
const getSavedQuestionType = (qid) => {
  const q = selectedSavedExam.value?.questions?.find((x) => x.id == qid);
  return q?.question_type?.name || "";
};
const getSavedQuestionAnswers = (qid) => {
  const q = selectedSavedExam.value?.questions?.find((x) => x.id == qid);
  return q?.answers || [];
};
const getSavedCorrectAnswers = (qid) => {
  const q = selectedSavedExam.value?.questions?.find((x) => x.id == qid);
  if (!q?.answers) return [];
  return q?.question_type?.name === "multiple_choice" ? q.answers.filter((a) => a.is_correct) : q.answers;
};
const getSavedTrueFalseAnswer = (qid) => {
  const a = getSavedQuestionAnswers(qid);
  if (!a.length) return t("exams.not_specified") || "غير محدد";
  return a[0].answer === "true" ? t("exams.true") || "صواب" : t("exams.false") || "خطأ";
};
const getSavedTextAnswer = (qid) => {
  const a = getSavedQuestionAnswers(qid);
  return a?.[0]?.answer || (t("exams.not_specified") || "غير محدد");
};
const getSavedQuestionNumber = (idx) => {
  if (!selectedSavedExam.value?.examStructure) return 1;
  let n = 1;
  for (let i = 0; i < idx; i++) if (selectedSavedExam.value.examStructure[i]?.type === "question") n++;
  return n;
};

/* ----------------------- Labels / Badges ----------------------- */
const getAnswerLabel = (i) => {
  const labels =
    [
      t("exams.a") || "أ",
      t("exams.b") || "ب",
      t("exams.c") || "ج",
      t("exams.d") || "د",
      t("exams.e") || "هـ",
      t("exams.f") || "و",
    ] || [];
  return labels[i] || String.fromCharCode(97 + i); // fallback a,b,c,...
};

const getQuestionTypeName = (id) => {
  const x = (questionTypes.value || []).find((t_) => t_.id == id);
  return x?.title || t("exams.unknown") || "غير معروف";
};

const getQuestionTypeDisplayName = (name) => {
  switch (name) {
    case "multiple_choice":
      return t("exams.multiple_choice") || "اختيار متعدد";
    case "true_false":
      return t("exams.true_false") || "صح/خطأ";
    case "short_answer":
      return t("exams.short_answer") || "إجابة قصيرة";
    case "essay":
      return t("exams.essay") || "مقال";
    case "matching":
      return t("exams.matching") || "مطابقة";
    default:
      return t("exams.unknown") || "غير معروف";
  }
};

const getQuestionLevelName = (id) => {
  const x = (questionLevels.value || []).find((l) => l.id == id);
  return x?.name || t("exams.unknown") || "غير معروف";
};

const getQuestionTypeClass = (name) => {
  const map = {
    multiple_choice: "bg-primary",
    true_false: "bg-warning text-dark",
    short_answer: "bg-info",
    essay: "bg-success",
    matching: "bg-secondary",
  };
  return map[name] || "bg-secondary";
};

const getQuestionLevelClass = (name) => {
  const map = {
    easy: "bg-success",
    medium: "bg-warning text-dark",
    hard: "bg-danger",
  };
  return map[name] || "bg-secondary";
};

/* ----------------------- Utilities ----------------------- */
const truncateText = (txt, len) => (!txt ? "" : txt.length > len ? txt.substring(0, len) + "..." : txt);

const getQuestionPreview = (qid) => {
  const q = (availableQuestions.value || []).find((x) => x.id == qid);
  if (!q) return t("exams.question_not_found") || "سؤال غير موجود";
  return truncateText(q.description, 50);
};

const isQuestionInStructure = (qid) =>
  examStructure.value.some((i) => i.type === "question" && i.question_id == qid);
const isQuestionUsedInStructure = isQuestionInStructure;

/* ----------------------- Printing / Export (CREATE VIEW) ----------------------- */
const printExam = () => {
  const win = window.open("", "_blank");
  if (!win) return;
  const model = makePrintModelFromCurrent();
  const content = (previewMode.value === "answers")
    ? generateProfessionalAnswerKeyContent(model)
    : generateProfessionalExamContent(model);

  win.document.write(`<!DOCTYPE html><html dir="rtl"><head><meta charset="UTF-8" /><title>${t("exams.print_exam_title") || "طباعة النموذج"}</title></head><body onload="window.print();window.close()">${content}</body></html>`);
  win.document.close();
};


const exportToPDF = async () => {
  try {
    toast.info(t("exams.preparing_pdf") || "جاري تحضير ملف PDF...", { timeout: 1800 });

    const model = makePrintModelFromCurrent();
    const content = (previewMode.value === "answers")
      ? generateProfessionalAnswerKeyContent(model)
      : generateProfessionalExamContent(model);
    const pdfContainer = document.createElement("div");
    Object.assign(pdfContainer.style, {
      position: "absolute",
      left: "-9999px",
      width: "210mm",
      padding: "1.5cm",
      fontFamily: "'Times New Roman', serif",
      lineHeight: "1.5",
      color: "#000",
      background: "white",
    });
    pdfContainer.innerHTML = content;

    document.body.appendChild(pdfContainer);

    const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

    const canvas = await html2canvas(pdfContainer, {
      scale: 2,
      logging: false,
      useCORS: true,
      allowTaint: true,
      scrollX: 0,
      scrollY: 0,
      width: pdfContainer.scrollWidth,
      height: pdfContainer.scrollHeight,
    });

    document.body.removeChild(pdfContainer);

    const imgData = canvas.toDataURL("image/png");
    const imgWidth = 210;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);

    const fileName =
      previewMode.value === "exam"
        ? `${t("exams.exam_file_prefix") || "امتحان"}-${examForm.value.title || t("exams.no_title") || "بدون-عنوان"}.pdf`
        : `${t("exams.answer_key_file_prefix") || "نموذج-إجابة"}-${examForm.value.title || t("exams.no_title") || "بدون-عنوان"}.pdf`;

    pdf.save(fileName);
    toast.success(t("exams.export_success") || "تم تصدير الملف بنجاح", { timeout: 2500 });
  } catch (e) {
    console.error("Error exporting to PDF:", e);
    toast.error(t("exams.export_error") || "حدث خطأ أثناء تصدير الملف", { timeout: 3000 });
  }
};

/* ---- HTML builders for printing (CREATE VIEW) ---- */

const generateProfessionalExamContent = (model) => {
  const getQText = (qid) => model.getQuestionById(qid)?.description || t("exams.no_question_text") || "نص السؤال غير متوفر";
  const getQType = (qid) => model.getQuestionById(qid)?.question_type?.name || "";
  const getQAnswers = (qid) => model.getQuestionById(qid)?.answers || [];

  const getNum = (idx) => {
    let n = 1;
    for (let i = 0; i < idx; i++) if (model.structure[i]?.type === "question") n++;
    return n;
  };

  let html = `
  <div class="professional-exam" style="font-family:'Times New Roman',serif;line-height:1.5;color:#000;background:#fff;padding:1.5cm;">
    <div class="header" style="text-align:center;margin-bottom:1.5rem;">
      <div class="exam-title" style="font-size:16pt;font-weight:bold;text-decoration:underline;margin-bottom:0.5rem;">
        ${model.title || t("exams.exam_title") || "عنوان الامتحان"}
      </div>
      <div class="exam-meta" style="font-size:12pt;margin-bottom:1rem;border-bottom:2px solid #000;padding-bottom:0.5rem;">
        <div style="float:right;">
          <strong>${t("exams.subject") || "المادة"}:</strong> ${getSubjectName(model.subject_id)} |
          <strong>${t("exams.teacher") || "المعلم"}:</strong> ${getTeacherName(model.teacher_id)}
        </div>
        <div style="float:left;">
          <strong>${t("exams.date") || "التاريخ"}:</strong> ${formatDate(model.date)} |
          <strong>${t("exams.level") || "المستوى"}:</strong> ${getDifficultyText(model.difficulty_level)}
        </div>
        <div style="clear:both;"></div>
      </div>
    </div>

    <!-- Student info (UNIFIED) -->
    <div class="student-info" style="border:1px solid #000;padding:0.5rem;margin-bottom:1.5rem;background:#f8f8f8;">
      <div style="display:flex;justify-content:space-between;margin-bottom:0.5rem;">
        <div style="flex:1;margin:0 0.5rem;"><strong>${t("exams.student_name") || "الاسم"}:</strong> _________________________</div>
        <div style="flex:1;margin:0 0.5rem;"><strong>${t("exams.class") || "الصف"}:</strong> _________________________</div>
      </div>
      <div style="display:flex;justify-content:space-between;">
        <div style="flex:1;margin:0 0.5rem;"><strong>${t("exams.date") || "التاريخ"}:</strong> _________________</div>
        <div style="flex:1;margin:0 0.5rem;"><strong>${t("exams.signature") || "التوقيع"}:</strong> _________________</div>
      </div>
    </div>

    <div class="content">
  `;

  model.structure.forEach((item, idx) => {
    if (item?.type === "title") {
      html += `
        <div class="section-title" style="font-weight:bold;font-size:14pt;margin:1.5rem 0 0.5rem;border-bottom:1px solid #000;padding-bottom:0.25rem;">
          ${item?.title_name || t("exams.section_title") || "عنوان القسم"}
        </div>
      `;
      return;
    }

    if (item?.type === "question") {
      const qNum = getNum(idx);
      const qText = getQText(item.question_id);
      const qType = getQType(item.question_id);
      const answers = getQAnswers(item.question_id);

      html += `
        <div class="question" style="margin-bottom:1.5rem;page-break-inside:avoid;">
          <div class="question-container">
            <span class="question-number" style="font-weight:bold;display:inline-block;min-width:1.5rem;">${qNum}.</span>
            <span class="question-text">${qText}</span>
      `;

      if (qType === "multiple_choice") {
        answers.forEach((a, i) => {
          html += `
            <div class="options" style="margin-top:0.5rem;margin-right:1.5rem;">
              <div class="option" style="margin-bottom:0.25rem;">
                <span class="option-circle" style="display:inline-block;width:12px;height:12px;border:1.5px solid #000;border-radius:50%;margin-left:8px;"></span>
                <span class="option-label" style="font-weight:bold;margin-left:4px;">${getAnswerLabel(i)})</span>
                ${a?.answer || ""}
              </div>
            </div>
          `;
        });
      } else if (qType === "true_false") {
        html += `
          <div class="options" style="margin-top:0.5rem;margin-right:1.5rem;">
            <div class="option" style="margin-bottom:0.25rem;">
              <span class="option-circle" style="display:inline-block;width:12px;height:12px;border:1.5px solid #000;border-radius:50%;margin-left:8px;"></span>
              <span class="option-label" style="font-weight:bold;margin-left:4px;">${t("exams.a") || "أ"})</span> ${t("exams.true") || "صواب"}
            </div>
            <div class="option" style="margin-bottom:0.25rem;">
              <span class="option-circle" style="display:inline-block;width:12px;height:12px;border:1.5px solid #000;border-radius:50%;margin-left:8px;"></span>
              <span class="option-label" style="font-weight:bold;margin-left:4px;">${t("exams.b") || "ب"})</span> ${t("exams.false") || "خطأ"}
            </div>
          </div>
        `;
      } else if (qType === "short_answer") {
        html += `
          <div class="answer-space" style="margin-top:0.5rem;">
            <div class="answer-line" style="border-bottom:1px solid #000;height:1.2em;margin-bottom:0.5rem;"></div>
          </div>
        `;
      } else if (qType === "essay") {
        html += `
          <div class="answer-space" style="margin-top:0.5rem;">
            ${[...Array(5)].map(() => `<div class="answer-line" style="border-bottom:1px solid #000;height:1.2em;margin-bottom:0.75rem;"></div>`).join("")}
          </div>
        `;
      } else if (qType === "matching") {
        html += `
          <div class="matching-instructions" style="margin-top:0.5rem;">
            <p style="font-weight:bold;margin-bottom:0.5rem;">${t("exams.matching_instruction") || "صل العمود (أ) بما يناسبه من العمود (ب)"}:</p>
            <table style="width:100%;border-collapse:collapse;margin-bottom:1rem;">
              <tr>
                <td style="width:50%;padding:0.25rem;vertical-align:top;">
                  ${answers.map((a,i)=> `<div style="margin-bottom:0.5rem;"><span style="font-weight:bold;">${i+1}.</span> ${a?.question_part || ""}</div>`).join("")}
                </td>
                <td style="width:50%;padding:0.25rem;vertical-align:top;">
                  ${answers.map((a,i)=> `<div style="margin-bottom:0.5rem;"><span style="font-weight:bold;">${getAnswerLabel(i)})</span> ${a?.answer || ""}</div>`).join("")}
                </td>
              </tr>
            </table>
          </div>
        `;
      }

      html += `</div></div>`;
    }
  });

  html += `
    </div>
    ${model.end_title ? `<div class="footer" style="text-align:center;margin-top:2rem;font-style:italic;">${model.end_title}</div>` : ""}
  </div>`;
  return html;
};

const generateProfessionalAnswerKeyContent = () => {
  let html = `
  <div class="professional-answer-key" style="font-family:'Times New Roman',serif;line-height:1.5;color:#000;background:#fff;padding:1.5cm;">
    <div class="header" style="text-align:center;margin-bottom:1.5rem;">
      <div class="title" style="font-size:16pt;font-weight:bold;margin-bottom:0.5rem;">${t("exams.answer_key") || "نموذج الإجابة"}</div>
      <div class="exam-title" style="font-size:14pt;text-decoration:underline;margin-bottom:1rem;">${examForm.value?.title || t("exams.exam_title") || "عنوان الامتحان"}</div>
    </div>
    <table style="width:100%;border-collapse:collapse;margin-top:1rem;">
      <thead>
        <tr>
          <th style="border:1px solid #000;padding:0.5rem;width:10%;">#</th>
          <th style="border:1px solid #000;padding:0.5rem;width:50%;">${t("exams.question") || "السؤال"}</th>
          <th style="border:1px solid #000;padding:0.5rem;width:15%;">${t("exams.type") || "النوع"}</th>
          <th style="border:1px solid #000;padding:0.5rem;width:25%;">${t("exams.correct_answer") || "الإجابة الصحيحة"}</th>
        </tr>
      </thead>
      <tbody>
  `;

  const qItems = examStructure.value.filter((i) => i?.type === "question");

  qItems.forEach((item) => {
    const qNum = getQuestionNumber(examStructure.value.indexOf(item));
    const qText = truncateText(getQuestionText(item?.question_id), 80);
    const qType = getQuestionType(item?.question_id);
    const typeDisplay = getQuestionTypeDisplayName(qType);

    let correct = "";
    if (qType === "multiple_choice") {
      const correctAnswers = getCorrectAnswers(item.question_id);
      const answers = getQuestionAnswers(item.question_id);
      correct = correctAnswers
        .map((a) => getAnswerLabel(answers.findIndex((x) => x.id === a.id)))
        .join(", ");
    } else if (qType === "true_false") {
      correct = getTrueFalseAnswer(item.question_id);
    } else if (qType === "short_answer" || qType === "essay") {
      correct = truncateText(getTextAnswer(item.question_id), 50);
    } else if (qType === "matching") {
      const answers = getQuestionAnswers(item.question_id);
      correct = answers.map((a, i) => `${i + 1}-${getAnswerLabel(i)}`).join(", ");
    } else {
      correct = t("exams.not_specified") || "غير محدد";
    }

    html += `
      <tr>
        <td style="border:1px solid #000;padding:0.5rem;text-align:center;">${qNum}</td>
        <td style="border:1px solid #000;padding:0.5rem;text-align:right;">${qText}</td>
        <td style="border:1px solid #000;padding:0.5rem;text-align:center;">${typeDisplay}</td>
        <td style="border:1px solid #000;padding:0.5rem;text-align:center;font-weight:bold;color:#006400;">${correct}</td>
      </tr>
    `;
  });

  html += `
      </tbody>
    </table>
  </div>
  `;
  return html;
};
const makePrintModelFromCurrent = () => ({
  title: examForm.value.title,
  subject_id: examForm.value.subject_id,
  teacher_id: examForm.value.teacher_id,
  date: examForm.value.date,
  difficulty_level: examForm.value.difficulty_level,
  end_title: examForm.value.end_title || "",
  structure: [...examStructure.value],
  // for current (create) view we read questions from availableQuestions
  getQuestionById: (qid) => (availableQuestions.value || []).find(q => q.id == qid) || null,
});

const makePrintModelFromSaved = (exam) => ({
  title: exam.title,
  subject_id: exam.subject_id,
  teacher_id: exam.teacher_id,
  date: exam.date,
  difficulty_level: exam.difficulty_level,
  end_title: exam.end_title || "",
  structure: [...(exam.structure || [])],
  // for saved view we read questions from selectedSavedExam.questions
  getQuestionById: (qid) => (selectedSavedExam.value?.questions || []).find(q => q.id == qid) || null,
});
/* ----------------------- Saved Exams: Print / Export ----------------------- */

const printSavedExam = (exam) => {
  const win = window.open("", "_blank");
  if (!win) return;
  const model = makePrintModelFromSaved(exam);
  const content = (savedExamPreviewMode.value === "answers")
    ? generateProfessionalAnswerKeyContent(model)
    : generateProfessionalExamContent(model);

  win.document.write(`<!DOCTYPE html><html dir="rtl"><head><meta charset="UTF-8" /><title>${t("exams.print_exam_title") || "طباعة النموذج"}</title></head><body onload="window.print();window.close()">${content}</body></html>`);
  win.document.close();
};
const exportSavedExamToPDF = async (exam) => {
  try {
    toast.info(t("exams.preparing_pdf") || "Preparing PDF...", { timeout: 1800 });

    // Ensure selectedSavedExam.questions is loaded (your selectSavedExam already does this)
    const model = makePrintModelFromSaved(exam);
    const mode = savedExamPreviewMode.value === "answers" ? "answers" : "exam";
    const content = (mode === "answers")
      ? generateProfessionalAnswerKeyContent(model)
      : generateProfessionalExamContent(model);
    // Off-screen container (like your create-view exporter)
    const pdfContainer = document.createElement("div");
    Object.assign(pdfContainer.style, {
      position: "absolute",
      left: "-9999px",
      width: "210mm",
      padding: "1.5cm",
      fontFamily: "'Times New Roman', serif",
      lineHeight: "1.5",
      color: "#000",
      background: "white",
    });
    pdfContainer.innerHTML = content;
    document.body.appendChild(pdfContainer);

    // Snapshot
    const canvas = await html2canvas(pdfContainer, {
      scale: 2,
      logging: false,
      useCORS: true,
      backgroundColor: "#ffffff",
      scrollX: 0,
      scrollY: 0,
      width: pdfContainer.scrollWidth,
      height: pdfContainer.scrollHeight,
    });

    document.body.removeChild(pdfContainer);

    // Build A4 PDF (multi-page if needed)
    const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const imgData = canvas.toDataURL("image/png");
    const pageWidth = 210;
    const pageHeight = 297;
    const imgHeight = (canvas.height * pageWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(imgData, "PNG", 0, position, pageWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, "PNG", 0, position, pageWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    const fileLabel = mode === "answers"
      ? (t("exams.answer_key_file_prefix") || "Answer-Key")
      : (t("exams.exam_file_prefix") || "Exam");

    pdf.save(`${exam.title || t("exams.no_title") || "Untitled"}-${fileLabel}.pdf`);
    toast.success(t("exams.export_success") || "Exported successfully", { timeout: 2500 });
  } catch (e) {
    console.error("Error exporting saved exam:", e);
    toast.error(t("exams.export_error") || "Failed to export PDF", { timeout: 3000 });
  }
};

const selectFirstSavedExam = () => {
  if (filteredSavedExams.value.length > 0) {
    selectSavedExam(filteredSavedExams.value[0]);
  } else {
    selectedSavedExam.value = null;
  }
};
/* ----------------------- Watches ----------------------- */
watch(currentView, (v) => {
  if (v === "saved" && !selectedSavedExam.value) {
    selectFirstSavedExam();
  }
});
watch(filteredSavedExams, (list) => {
  if (!list.length) {
    selectedSavedExam.value = null;
    return;
  }
  const stillVisible = list.some(ex => ex.id === selectedSavedExam.value?.id);
  if (!stillVisible) selectFirstSavedExam();
});


watch(
  () => examForm.value.teacher_id,
  (v) => {
    if (v && examForm.value.subject_id) fetchQuestionsBySubject();
    else availableQuestions.value = [];
  }
);

watch(
  () => examForm.value.class_id,
  async (v) => {
    if (!v) {
      subjects.value = [];
      examForm.value.subject_id = "";
      examForm.value.teacher_id = "";
      availableQuestions.value = [];
      return;
    }
    await handleClassChange();
  }
);

watch(savedExamsClassFilter, (v) => {
  if (!v) savedExamsSubjectFilter.value = "";
});

/* ----------------------- Lifecycle ----------------------- */
onMounted(async () => {
  // Local cache (optional, kept if you want the count visible immediately)
  const saved = localStorage.getItem("savedExams");
  if (saved) {
    try {
      const list = JSON.parse(saved);
      if (Array.isArray(list)) savedExams.value = list;
    } catch (_) {
      savedExams.value = [];
    }
  }

  if (isTeacher.value) {
    await fetchTeacherData();
  } else {
    await fetchInitialData();
  }

  // Always refresh from backend after boot
  fetchSavedExams();
});
</script>

<style scoped>
.professional-exam {
  font-family: "Times New Roman", serif;
  line-height: 1.5;
  color: #000;
  background: white;
  padding: 1.5cm;
}

.professional-exam .header {
  text-align: center;
  margin-bottom: 1.5rem;
}

.professional-exam .school-name {
  font-size: 18pt;
  font-weight: bold;
  margin-bottom: 0.5rem;
}

.professional-exam .exam-title {
  font-size: 16pt;
  font-weight: bold;
  text-decoration: underline;
  margin-bottom: 0.5rem;
}

.professional-exam .exam-meta {
  font-size: 12pt;
  margin-bottom: 1rem;
  border-bottom: 2px solid #000;
  padding-bottom: 0.5rem;
}

.professional-exam .student-info {
  border: 1px solid #000;
  padding: 0.5rem;
  margin-bottom: 1.5rem;
  background: #f8f8f8;
}

.professional-exam .student-info-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}

.professional-exam .student-info-item {
  flex: 1;
  margin: 0 0.5rem;
}

.professional-exam .section-title {
  font-weight: bold;
  font-size: 14pt;
  margin: 1.5rem 0 0.5rem 0;
  border-bottom: 1px solid #000;
  padding-bottom: 0.25rem;
}

.professional-exam .question {
  margin-bottom: 1.5rem;
  page-break-inside: avoid;
}

.professional-exam .question-number {
  font-weight: bold;
  display: inline-block;
  min-width: 1.5rem;
}

.professional-exam .question-text {
  display: inline;
}

.professional-exam .options {
  margin-top: 0.5rem;
  margin-right: 1.5rem;
}

.professional-exam .option {
  margin-bottom: 0.25rem;
}

.professional-exam .option-circle {
  display: inline-block;
  width: 12px;
  height: 12px;
  border: 1.5px solid #000;
  border-radius: 50%;
  margin-left: 8px;
}

.professional-exam .option-label {
  font-weight: bold;
  margin-left: 4px;
}

.professional-exam .answer-space {
  margin-top: 0.5rem;
}

.professional-exam .answer-line {
  border-bottom: 1px solid #000;
  height: 1.2em;
  margin-bottom: 0.5rem;
}

.professional-exam .essay-lines .answer-line {
  margin-bottom: 1rem;
}

.professional-exam .footer {
  text-align: center;
  margin-top: 2rem;
  font-style: italic;
}

/* Answer Key Styles */
.professional-answer-key {
  font-family: "Times New Roman", serif;
  line-height: 1.5;
  color: #000;
  background: white;
  padding: 1.5cm;
}

.professional-answer-key .header {
  text-align: center;
  margin-bottom: 1.5rem;
}

.professional-answer-key .title {
  font-size: 16pt;
  font-weight: bold;
  margin-bottom: 0.5rem;
}

.professional-answer-key .exam-title {
  font-size: 14pt;
  text-decoration: underline;
  margin-bottom: 1rem;
}

.professional-answer-key table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
}

.professional-answer-key th,
.professional-answer-key td {
  border: 1px solid #000;
  padding: 0.5rem;
  text-align: center;
}

.professional-answer-key th {
  background-color: #f0f0f0;
  font-weight: bold;
}

.professional-answer-key .correct-answer {
  font-weight: bold;
  color: #006400;
}

/* Print-specific styles */
@media print {
  body {
    background: white !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  .professional-exam,
  .professional-answer-key {
    padding: 1.5cm !important;
    margin: 0 !important;
    box-shadow: none !important;
  }

  .no-print {
    display: none !important;
  }

  .page-break {
    page-break-after: always;
  }
}

/* Step Indicators */
.step-indicator {
  margin: 0 1rem;
}

.step-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #e9ecef;
  color: #6c757d;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  transition: all 0.3s ease;
}

.step-indicator.active .step-circle {
  background: #2c91ae;
  color: white;
}

.step-indicator.completed .step-circle {
  background: #198754;
  color: white;
}

.step-label {
  font-size: 0.9rem;
  color: #6c757d;
  white-space: nowrap;
}

.step-indicator.active .step-label {
  color: #2c91ae;
  font-weight: 600;
}

.step-indicator.completed .step-label {
  color: #198754;
}

.step-line {
  width: 60px;
  height: 2px;
  background: #e9ecef;
}

.step-indicator.completed + .step-indicator .step-line {
  background: #198754;
}

/* Structure Builder */
.structure-item {
  transition: all 0.2s ease;
  background: #f8f9fa;
}

.structure-item:hover {
  background: #e9ecef;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.drag-handle:hover {
  color: #2c91ae !important;
}

/* Questions List */
.question-card {
  transition: all 0.2s ease;
  background: #f8f9fa;
}

.question-card:hover {
  background: #e9ecef;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Question Modal */
.question-modal-card {
  transition: all 0.2s ease;
  background: #f8f9fa;
}

.question-modal-card:hover {
  background: #e9ecef;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Saved Exams */
.saved-exam-card {
  transition: all 0.2s ease;
  background: #f8f9fa;
}

.saved-exam-card:hover {
  background: #e9ecef;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.cursor-pointer {
  cursor: pointer;
}

/* Paper Preview */
.paper-container,
.saved-paper-container {
  background: #f8f9fa;
}

.exam-paper,
.answer-key {
  background: white;
  font-family: "Arial", sans-serif;
  line-height: 1.6;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.1);
  margin: 1rem;
  border-radius: 8px;
}

.student-info-section {
  background: #f8f9fa;
  border: 2px solid #dee2e6;
}

.student-line {
  display: inline-block;
  min-width: 120px;
  padding-bottom: 2px;
}

.exam-title {
  color: #2c3e50;
  border-bottom: 3px solid #2c91ae;
  padding-bottom: 0.5rem;
}

.question-number {
  color: #2c91ae;
  font-size: 1.1rem;
  min-width: 30px;
}

.answer-circle {
  width: 12px;
  height: 12px;
  border: 2px solid #6c757d;
  border-radius: 50%;
  background: white;
}

.answer-line-border {
  border-bottom: 1px solid #6c757d;
  height: 20px;
  margin-bottom: 8px;
}

.essay-lines .answer-line-border {
  margin-bottom: 12px;
}

.answer-label {
  color: #495057;
}

.pdf-export {
  background: white !important;
  padding: 20px !important;
  box-shadow: none !important;
  height: auto !important;
  overflow: visible !important;
}

.is-invalid {
  border-color: #dc3545;
}

.invalid-feedback {
  display: block;
}

@media (max-width: 768px) {
  .step-indicator {
    margin: 0 0.5rem;
  }

  .step-label {
    display: none;
  }

  .step-line {
    width: 30px;
  }
}

@media (max-width: 992px) {
  .container-fluid {
    padding-left: 15px;
    padding-right: 15px;
  }

  .card {
    margin-bottom: 1rem;
  }

  .structure-item,
  .question-card {
    padding: 0.75rem;
  }

  .step-indicator {
    margin: 0 0.5rem;
  }

  .step-label {
    font-size: 0.8rem;
  }

  .step-line {
    width: 30px;
  }
}

@media (max-width: 768px) {
  .row {
    flex-direction: column;
  }

  .col-md-5,
  .col-md-7,
  .col-md-4,
  .col-md-8 {
    width: 100%;
    max-width: 100%;
    flex: 0 0 100%;
  }

  .step-indicator {
    margin: 0 0.3rem;
  }

  .step-label {
    display: none;
  }

  .step-line {
    width: 20px;
  }

  .exam-meta.row > div {
    width: 100%;
    text-align: center !important;
    margin-bottom: 0.5rem;
  }

  .btn-group {
    flex-wrap: wrap;
  }

  .btn-group .btn {
    margin-bottom: 0.5rem;
  }

  .structure-item input,
  .structure-item select {
    width: 100% !important;
    min-width: auto !important;
  }
}

@media (max-width: 576px) {
  .step-circle {
    width: 30px;
    height: 30px;
    font-size: 0.9rem;
  }

  .step-line {
    width: 15px;
  }

  .exam-header h3 {
    font-size: 1.5rem;
  }

  .section-title {
    font-size: 1.2rem;
  }

  .question-number {
    min-width: 25px;
  }

  .badge {
    font-size: 0.7rem;
    padding: 0.35em 0.65em;
  }

  .paper-container,
  .saved-paper-container {
    height: 500px !important;
  }

  .questions-list {
    max-height: 300px !important;
  }
}

@media print {
  .no-print {
    display: none !important;
  }

  body {
    padding: 0 !important;
    margin: 0 !important;
    background: white !important;
  }

  .paper-container,
  .saved-paper-container {
    height: auto !important;
    overflow: visible !important;
    padding: 0 !important;
    margin: 0 !important;
  }

  .exam-paper,
  .answer-key {
    box-shadow: none !important;
    margin: 0 !important;
    padding: 20px !important;
    page-break-after: always;
  }
}
.drag-handle { cursor: grab; user-select: none; }
.drag-over { outline: 2px dashed #2c91ae; background-color: #eef7fb; }

</style>
